import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { SpaceData } from "~/lib/types";
import {
  useUpdateSpaceMutation,
  UpdateSpaceInput,
  useLoadSpaceLazyQuery,
} from "~/operations";

// Read model = LoadSpace { SpaceSettings }

// Write model = UpdateSpaceInput { SpaceSettingsInput }

// Form model
type SpaceSettingsFieldValues = {
  // Space ID
  id: string;
  // Display name
  name: string;
  // Description
  description: string;
  // Remove terminated assets
  terminatedAssets: number;
  // Remove inactive assets
  inactiveAssets: number;
  // End-of-life assets
  eolAssets: number;
  // Vulnerability scanning
  vulnerabilityScanning: string;
  // Include MQL vulnerabilities
  mqlVulnerabilities: string;
  // Include experimental sources
  experimentalSources: string[];
  // Require exception approvals
  exceptionsRequireApproval: boolean;
  // Allow users to approve their own exceptions
  exceptionsAllowSelfApproval: boolean;
  // Allow user to set indefinite exception
  exceptionsAllowIndefiniteValidUntil: boolean;
};

// Read model => form model
function settingsDataToFieldValues(
  space?: SpaceData | null,
): SpaceSettingsFieldValues {
  const { settings } = space || {};
  return {
    id: space?.id ?? "",
    name: space?.name ?? "",
    description: space?.description ?? "",
    terminatedAssets: settings?.terminatedAssetsConfiguration.cleanup ? 1 : 0,
    inactiveAssets: settings?.garbageCollectAssetsConfiguration.afterDays ?? 0,
    eolAssets: settings?.eolAssetsConfiguration.monthsInAdvance ?? 6,
    vulnerabilityScanning: settings?.platformVulnerabilityConfiguration.enable
      ? "enabled"
      : "disabled",
    mqlVulnerabilities: settings?.platformMqlVexConfiguration.enable
      ? "enabled"
      : "disabled",
    experimentalSources:
      settings?.mvdExperimentalSourcesConfiguration.sources?.flatMap((a) =>
        a.enabled ? a.id : [],
      ) ?? [],
    exceptionsRequireApproval:
      settings?.exceptionsConfiguration?.requireApproval ?? true,
    exceptionsAllowSelfApproval:
      settings?.exceptionsConfiguration?.allowSelfApproval ?? false,
    exceptionsAllowIndefiniteValidUntil:
      settings?.exceptionsConfiguration?.allowIndefiniteValidUntil ?? true,
  };
}

// Form model => write model
function settingsFieldValuesToInput(
  mrn: string,
  fieldValues: SpaceSettingsFieldValues,
): UpdateSpaceInput {
  return {
    mrn,
    name: fieldValues.name,
    description: fieldValues.description,
    settings: {
      terminatedAssetsConfiguration: {
        cleanup: fieldValues.terminatedAssets > 0,
      },
      garbageCollectAssetsConfiguration: {
        enable: fieldValues.inactiveAssets > 0,
        afterDays: fieldValues.inactiveAssets,
      },
      eolAssetsConfiguration: {
        enable: fieldValues.eolAssets > 0,
        monthsInAdvance: fieldValues.eolAssets,
      },
      platformVulnerabilityConfiguration: {
        enable: fieldValues.vulnerabilityScanning === "enabled",
      },
      platformMqlVexConfiguration: {
        enable: fieldValues.mqlVulnerabilities === "enabled",
      },
      mvdExperimentalSourcesConfiguration: {
        enabledSources: fieldValues.experimentalSources.map((id) => ({ id })),
      },
      exceptionsConfiguration: {
        requireApproval: fieldValues.exceptionsRequireApproval,
        allowSelfApproval: fieldValues.exceptionsAllowSelfApproval,
        allowIndefiniteValidUntil:
          fieldValues.exceptionsAllowIndefiniteValidUntil,
      },
    },
  };
}

export interface UseSpaceSettingsFormProps {
  spaceMrn: string;
}

export function useSpaceSettingsForm({ spaceMrn }: UseSpaceSettingsFormProps) {
  const [updateSpace, updateSpaceResult] = useUpdateSpaceMutation();
  const [loadSpace, loadSpaceResult] = useLoadSpaceLazyQuery({
    variables: { spaceMrn },
  });

  const space = loadSpaceResult.data?.space;
  const settings = space?.settings;

  const form = useForm<SpaceSettingsFieldValues>({
    defaultValues: async () => {
      const { data } = await loadSpace();
      return settingsDataToFieldValues(data?.space);
    },
  });

  useEffect(() => {
    form.reset(settingsDataToFieldValues(space));
  }, [space]);

  const onSubmit = useCallback(
    async (values: SpaceSettingsFieldValues) => {
      if (!form.formState.isDirty || !form.formState.isValid) return;

      try {
        // Build the input
        const input = settingsFieldValuesToInput(spaceMrn, values);

        // Save the input
        await updateSpace({ variables: { input } });

        // Reset isDirty
        form.reset(values);

        // Notify the user
        enqueueSnackbar("Space successfully updated", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Failed to update space", { variant: "error" });
      }
    },
    [spaceMrn, form.formState.isDirty, form.formState.isValid],
  );

  const fieldIsUpdating = useCallback(
    (name: keyof SpaceSettingsFieldValues) => {
      return updateSpaceResult.loading && form.getFieldState(name).isDirty;
    },
    [updateSpaceResult.loading, form.getFieldState],
  );

  return {
    settings,
    form,
    onSubmit,
    fieldIsUpdating,
  };
}

export type SpaceSettingsForm = ReturnType<typeof useSpaceSettingsForm>;
